import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { TimezonePickerComponent } from '@shared/components/timezone-picker/timezone-picker.component';
import { CalendarService, ICalendar } from '@shared/services/calendar.service';
import { IBreakViewModel, ITeamProfile, ITeamProfileConfiguration, ITeamProfileConfigurationViewModel, ITimeInterval, IWeekDays, IWorkDay, TeamProfileService } from '@shared/services/teamProfiles.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-create-update-team-profile',
	templateUrl: 'create-update-team-profile.html',
	styleUrls: ['create-update-team-profile.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateTeamProfileDialogComponent implements OnDestroy, AfterViewInit {
	@Output() saved: EventEmitter<any> = new EventEmitter();
	@ViewChild('timezonePicker') timezonePicker: TimezonePickerComponent;
	private destroy$: Subject<void> = new Subject<void>();
	form: FormGroup;
	configForm: FormGroup;
	teamProfile: ITeamProfile;
	teamProfileConfiguration: ITeamProfileConfigurationViewModel;

	configurations = [];
	isEdit = false;
	httpError: string = '';

	calendars: ICalendar[] = [];

	activeDate;
	weekDays: IWeekDays;
	TeamProfileConfigurations: ITeamProfileConfiguration[];
	showError = false;
	selectedConfiguration;
	editableConfiguration;
	configurationEditInProgress: boolean = false;
	formerSelectedConfig: number = -1;
	editingExistingConfig: boolean = false;

	workTimeError = false;
	breakTimeError = false;
	duplicateError = false;
	noConfigurationsError = false;

	displayedColumns: string[] = ['break-start', 'break-end', 'remove-break'];

	daysInWeek = [
		{
			dayName: 'Monday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Tuesday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Wednesday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Thursday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Friday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Saturday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
		{
			dayName: 'Sunday',
			isWorkDay: false,
			startTime: '',
			endTime: '',
			vwwStartTime: '',
			vwwEndTime: '',
			breaks: new MatTableDataSource<IBreakViewModel>(),
			startTimeError: false,
			endTimeError: false,
			vwwStartTimeError: false,
			vwwEndTimeError: false,
			breakStart: '',
			breakEnd: '',
			breakError: true,
		},
	];

	private destroyNotifier$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<CreateUpdateTeamProfileDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public editTeamProfile: ITeamProfile,
		public teamProfileService: TeamProfileService,
		public calendarService: CalendarService,
		public dialog: MatDialog,
		private fb: FormBuilder,
		private configfb: FormBuilder,
		private translator: TranslateService,
		private cdr: ChangeDetectorRef,
	) {
		if (editTeamProfile != null) {
			this.isEdit = true;
			this.teamProfile = { ...this.editTeamProfile };

			this.TeamProfileConfigurations = editTeamProfile.configurations ?? [];

			this.TeamProfileConfigurations.forEach((config) => {
				this.daysInWeek.forEach((day) => {
					const weekDay: IWorkDay = config.weekDays[day.dayName.toLowerCase()];

					if (weekDay.workTime == null) {
						weekDay.workTime = {
							startTime: '',
							endTime: '',
						};
					}
					if (weekDay.variableWeeklyWorkTime == null) {
						weekDay.variableWeeklyWorkTime = {
							startTime: '',
							endTime: '',
						};
					}
				});
			});

			const today = new Date().toISOString().split('T')[0];
			const configurationIndex = this.TeamProfileConfigurations.sort((a, b) => (a.activeDate > b.activeDate ? -1 : 1)).findIndex((c) => c.activeDate <= today);
			this.selectedConfiguration = configurationIndex > -1 ? configurationIndex : 0;
			const currentConfig = this.TeamProfileConfigurations[this.selectedConfiguration];
			this.teamProfileConfiguration = {
				activeDate: currentConfig.activeDate,
				roundingTime: currentConfig.roundingTime,
				endRoundingTime: currentConfig.endRoundingTime,
				configAlias: currentConfig.alias,
			};
		} else {
			this.TeamProfileConfigurations = [];
			this.teamProfile = {
				id: null,
				alias: '',
				calendarId: '',
				description: '',
				timeZoneId: null,
				configurations: [],
			};
			this.teamProfileConfiguration = {
				activeDate: '',
				roundingTime: 0,
				endRoundingTime: 0,
				configAlias: '',
			};
		}
	}

	ngOnInit(): void {
		this.calendarService.getCalendars().subscribe((c) => {
			this.calendars = c.result;
			if (!this.calendars.some((cal) => this.teamProfile.calendarId === cal.id)) {
				this.form.patchValue({ calendarId: '' });
			}
		});
		this.initializeForms();
	}

	initializeForms(): void {
		this.form = this.fb.group({
			alias: [this.teamProfile.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			description: [this.teamProfile.description, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			calendarId: [this.teamProfile.calendarId, [Validators.required]],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.teamProfile) {
					this.teamProfile[key] = value[key];
				}
			});
		});

		this.configForm = this.configfb.group({
			activeDate: [this.teamProfileConfiguration.activeDate, [Validators.required]],
			roundingTime: [this.teamProfileConfiguration.roundingTime, [Validators.required]],
			endRoundingTime: [this.teamProfileConfiguration.endRoundingTime, [Validators.required]],
			configAlias: [this.teamProfileConfiguration.configAlias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
		});
		this.configForm.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.teamProfileConfiguration) {
					this.teamProfileConfiguration[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	get configControls() {
		return this.configForm.controls;
	}

	onSelected(selectedConfiguration): void {
		this.selectedConfiguration = selectedConfiguration;
		this.updateConfigurationForms();
	}

	addConfiguration(): void {
		this.formerSelectedConfig = this.selectedConfiguration;
		this.configurationEditInProgress = true;
		this.editingExistingConfig = false;
		this.noConfigurationsError = false;

		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);
		const tomorrowStr = tomorrow.toISOString().split('T')[0];

		this.teamProfileConfiguration = {
			...this.teamProfileConfiguration,
			activeDate: tomorrowStr,
		};

		this.selectedConfiguration = -1;
		this.daysInWeek.forEach((day) => {
			const data = [...day.breaks.data];
			day.breaks = new MatTableDataSource<IBreakViewModel>();
			day.breaks.data = data;
		});
		this.configForm.patchValue({
			configAlias: this.teamProfileConfiguration.configAlias,
			roundingTime: this.teamProfileConfiguration.roundingTime,
			endRoundingTime: this.teamProfileConfiguration.endRoundingTime,
			activeDate: this.teamProfileConfiguration.activeDate,
		});
	}

	editConfiguration(): void {
		this.formerSelectedConfig = this.selectedConfiguration;
		this.configurationEditInProgress = true;
		this.editingExistingConfig = true;
	}

	removeConfiguration(): void {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('mes.administration.team-profiles.dialog.remove-configuration.header'),
				content: this.translator.instant('mes.administration.team-profiles.dialog.remove-configuration.content'),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.TeamProfileConfigurations.splice(this.selectedConfiguration, 1);
					this.configForm.patchValue({
						configAlias: '',
						roundingTime: 0,
						endRoundingTime: 0,
						activeDate: '',
					});
					this.resetWorkdays();
					if (this.selectedConfiguration < this.TeamProfileConfigurations.length) {
						this.onSelected(this.selectedConfiguration);
					} else if (this.selectedConfiguration - 1 < this.TeamProfileConfigurations.length) {
						this.onSelected(this.selectedConfiguration - 1);
					} else {
						this.onSelected(0);
					}
					if (this.TeamProfileConfigurations.length < 1) {
						this.noConfigurationsError = true;
					}
				}
			});
	}

	saveConfiguration(): void {
		const dateObj = this.configForm.controls['activeDate'].value;
		const dateStr = dateObj instanceof Date ? this.parseDate(dateObj) : dateObj;
		this.teamProfile.configurations = this.TeamProfileConfigurations ? this.TeamProfileConfigurations : [];
		this.teamProfile.timeZoneId ??= this.timezonePicker.timeZoneId;
		if (this.doesConfigurationsContainDuplicate(this.teamProfile.configurations, this.teamProfileConfiguration)) {
			return;
		}

		const newConfig: ITeamProfileConfiguration = {
			alias: this.teamProfileConfiguration.configAlias,
			activeDate: dateStr,
			weekDays: this.parseWeekData(this.daysInWeek),
			roundingTime: this.teamProfileConfiguration.roundingTime,
			endRoundingTime: this.teamProfileConfiguration.endRoundingTime,
		};

		if (this.editingExistingConfig) {
			this.teamProfile.configurations[this.selectedConfiguration] = newConfig;
		} else {
			this.teamProfile.configurations.push(newConfig);
		}

		this.configForm.reset();
		this.selectedConfiguration = this.editingExistingConfig ? this.formerSelectedConfig : this.teamProfile.configurations.length - 1;
		this.editingExistingConfig = false;
		this.formerSelectedConfig = -1;
		this.updateConfigurationForms();
		this.configurationEditInProgress = false;
	}

	cancelConfiguration(): void {
		this.resetWorkdays();
		this.configForm.reset();
		this.selectedConfiguration = this.formerSelectedConfig;
		this.editingExistingConfig = false;
		this.formerSelectedConfig = -1;
		this.updateConfigurationForms();

		this.noConfigurationsError = this.TeamProfileConfigurations.length < 1;

		this.configurationEditInProgress = false;
	}

	onTimeZoneChange(event: { country: string; region: string; timeZoneId: string | null }) {
		this.teamProfile.timeZoneId = event.timeZoneId;
	}

	updateConfigurationForms(): void {
		const configurations = this.TeamProfileConfigurations;
		const selectedConfiguration = this.selectedConfiguration;

		this.editableConfiguration = this.TeamProfileConfigurations[selectedConfiguration]?.activeDate > new Date().toISOString().split('T')[0];

		if (configurations && typeof selectedConfiguration != 'undefined' && configurations.length > 0) {
			this.configForm.patchValue({
				configAlias: configurations[selectedConfiguration].alias,
				roundingTime: configurations[selectedConfiguration].roundingTime,
				endRoundingTime: configurations[selectedConfiguration].endRoundingTime,
				activeDate: configurations[selectedConfiguration].activeDate,
			});

			this.daysInWeek.forEach((day) => {
				const dayData = configurations[selectedConfiguration].weekDays[day.dayName.toLowerCase()];
				day.isWorkDay = dayData.isWorkDay;
				day.startTime = dayData.workTime?.startTime;
				day.endTime = dayData.workTime?.endTime;
				day.vwwStartTime = this.toTimeWithoutSeconds(dayData.variableWeeklyWorkTime?.startTime) ?? '';
				day.vwwEndTime = this.toTimeWithoutSeconds(dayData.variableWeeklyWorkTime?.endTime) ?? '';
				day.breaks.data = dayData.breaks;

				const dayId = `day-${day.dayName.toLowerCase()}`;
				const dayElement = document.getElementById(dayId);

				const startTimeElement = <HTMLInputElement>dayElement.getElementsByClassName('workday-start-time')[0];
				startTimeElement.value = day.startTime;

				const endTimeElement = <HTMLInputElement>dayElement.getElementsByClassName('workday-end-time')[0];
				endTimeElement.value = day.endTime;

				const vwwStartElement = <HTMLInputElement>dayElement.getElementsByClassName('workday-vww-start-time')[0];
				if (vwwStartElement) vwwStartElement.value = day.vwwStartTime;

				const vwwEndElement = <HTMLInputElement>dayElement.getElementsByClassName('workday-vww-end-time')[0];
				if (vwwEndElement) vwwEndElement.value = day.vwwEndTime;
			});
		}
	}

	resetWorkdays() {
		this.daysInWeek.forEach((day) => {
			day.isWorkDay = false;
			day.startTime = '';
			day.endTime = '';
			day.vwwStartTime = '';
			day.vwwEndTime = '';
			day.breaks.data = [];
			this.validateWorkTimes(day);
		});
		const elements = document.querySelectorAll<HTMLInputElement>('.workday-start-time, .workday-end-time, .workday-vww-start-time, .workday-vww-end-time');
		elements.forEach((element) => {
			element.value = '';
		});
	}

	ngAfterViewInit(): void {
		this.updateConfigurationForms();
		this.cdr.detectChanges();
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	addBreak(day) {
		const data = day.breaks.data;
		data.push({ startTime: day.breakStart, endTime: day.breakEnd });
		data.sort((a, b) => a.startTime.localeCompare(b.startTime));
		day.breaks.data = data;

		day.breakStart = '';
		day.breakEnd = '';
		this.validateBreakTimes(day);
	}

	removeBreak(index: number, day) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('mes.administration.team-profiles.dialog.remove-break.header'),
				content: this.translator.instant('mes.administration.team-profiles.dialog.remove-break.content'),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					const data = day.breaks.data;
					data.splice(index, 1);
					day.breaks.data = data;
				}
			});
	}

	roundingStartChanged(event: any) {
		const endRoundingTime = this.teamProfileConfiguration.endRoundingTime;
		const newValue = event.target.valueAsNumber;

		if (Number.isNaN(newValue)) this.teamProfileConfiguration.roundingTime = 0;

		if (endRoundingTime == null || Number.isNaN(endRoundingTime)) {
			this.form.patchValue({ endRoundingTime: newValue });
		}
	}

	toggleIsWorkDay(checked: boolean, day) {
		day.isWorkDay = checked;
		this.validateWorkTimes(day);
	}

	changeDayStartTime(event: any, day) {
		day.startTime = event.target.value;
		this.validateWorkTimes(day);
	}

	changeDayEndTime(event: any, day) {
		day.endTime = event.target.value;
		this.validateWorkTimes(day);
	}

	changeDayVwwStartTime(event: any, day) {
		day.vwwStartTime = event.target.value;
		this.validateWorkTimes(day);
	}

	changeDayVwwEndTime(event: any, day) {
		day.vwwEndTime = event.target.value;
		this.validateWorkTimes(day);
	}

	changeBreakStartTime(event: any, day) {
		if (day) {
			day.breakStart = event.target.value;
		}
		this.validateBreakTimes(day);
	}

	changeBreakEndTime(event: any, day) {
		if (day) {
			day.breakEnd = event.target.value;
		}
		this.validateBreakTimes(day);
	}

	doesConfigurationsContainDuplicate(configurations, newConfig): boolean {
		this.duplicateError = false;
		const newConfigDateStr = newConfig.activeDate instanceof Date ? this.parseDate(newConfig.activeDate) : newConfig.activeDate;

		if (configurations.some((c, idx) => (this.editingExistingConfig && idx !== this.formerSelectedConfig && c.activeDate === newConfigDateStr) || (!this.editingExistingConfig && c.activeDate === newConfigDateStr))) {
			this.duplicateError = true;
			return true;
		}
		return false;
	}

	saveTeamProfile() {
		this.TeamProfileConfigurations.forEach((conf) => {
			if (conf.roundingTime == null || Number.isNaN(conf.roundingTime)) {
				conf.roundingTime = 0;
			}
			if (conf.endRoundingTime == null || Number.isNaN(conf.endRoundingTime)) {
				conf.endRoundingTime = conf.roundingTime;
			}

			conf.weekDays.monday.workTime = conf.weekDays.monday.workTime.startTime === '' && conf.weekDays.monday.workTime.endTime === '' ? null : conf.weekDays.monday.workTime;
			conf.weekDays.tuesday.workTime = conf.weekDays.tuesday.workTime.startTime === '' && conf.weekDays.tuesday.workTime.endTime === '' ? null : conf.weekDays.tuesday.workTime;
			conf.weekDays.wednesday.workTime = conf.weekDays.wednesday.workTime.startTime === '' && conf.weekDays.wednesday.workTime.endTime === '' ? null : conf.weekDays.wednesday.workTime;
			conf.weekDays.thursday.workTime = conf.weekDays.thursday.workTime.startTime === '' && conf.weekDays.thursday.workTime.endTime === '' ? null : conf.weekDays.thursday.workTime;
			conf.weekDays.friday.workTime = conf.weekDays.friday.workTime.startTime === '' && conf.weekDays.friday.workTime.endTime === '' ? null : conf.weekDays.friday.workTime;
			conf.weekDays.saturday.workTime = conf.weekDays.saturday.workTime.startTime === '' && conf.weekDays.saturday.workTime.endTime === '' ? null : conf.weekDays.saturday.workTime;
			conf.weekDays.sunday.workTime = conf.weekDays.sunday.workTime.startTime === '' && conf.weekDays.sunday.workTime.endTime === '' ? null : conf.weekDays.sunday.workTime;

			const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
			days.forEach((dayName) => {
				const day = conf.weekDays[dayName];
				const vww = day.variableWeeklyWorkTime;
				const isVwwEmpty = !vww || ((vww.startTime === '' || vww.startTime === '00:00:00') && (vww.endTime === '' || vww.endTime === '00:00:00'));
				day.variableWeeklyWorkTime = isVwwEmpty ? null : vww;
			});
		});

		if (this.TeamProfileConfigurations.length < 1) {
			this.noConfigurationsError = true;
			return;
		}

		if (this.teamProfile.id == null) {
			this.createTeamProfile(this.teamProfile);
		} else {
			this.updateTeamProfile(this.teamProfile);
		}
	}

	getFirstValidationErrorName(form: string, formControlName: string) {
		switch (form) {
			case 'form':
				return Object.keys(this.form.controls[formControlName].errors)[0];
			case 'configForm':
				return Object.keys(this.configForm.controls[formControlName].errors)[0];
		}
		return null;
	}

	validateWorkTimes(day) {
		day.startTimeError = day.startTime === '' && (day.endTime !== '' || day.isWorkDay);
		day.endTimeError = day.endTime === '' && (day.startTime !== '' || day.isWorkDay);

		// VWW is optional — no required validation, only range check
		day.vwwStartTimeError = false;
		day.vwwEndTimeError = false;

		// VWW must be within work time (supports shifts crossing midnight)
		if (day.startTime && day.endTime) {
			const startM = this.timeToMinutes(day.startTime);
			const endM = this.toShiftMinutes(day.endTime, day.startTime);

			let vwwStartM: number | null = null;
			let vwwEndM: number | null = null;

			if (day.vwwStartTime) {
				vwwStartM = this.toShiftMinutes(day.vwwStartTime, day.startTime);
				day.vwwStartTimeError = vwwStartM < startM || vwwStartM > endM;
			}
			if (day.vwwEndTime) {
				vwwEndM = this.toShiftMinutes(day.vwwEndTime, day.startTime);
				day.vwwEndTimeError = vwwEndM < startM || vwwEndM > endM;
			}

			if (vwwStartM != null && vwwEndM != null && vwwEndM < vwwStartM) {
				day.vwwStartTimeError = true;
				day.vwwEndTimeError = true;
			}
		}

		this.workTimeError = this.daysInWeek.some((d) => d.startTimeError || d.endTimeError || d.vwwStartTimeError || d.vwwEndTimeError);
	}

	private normalizeTime(time: string): string {
		const [h, m] = time.split(':');
		return `${h.padStart(2, '0')}:${(m ?? '00').padStart(2, '0')}`;
	}

	private timeToMinutes(time: string): number {
		const [h, m] = this.normalizeTime(time).split(':').map(Number);
		return h * 60 + m;
	}

	// Maps a time onto the shift timeline. If the shift crosses midnight
	// and the time is before the shift start, it belongs to the next day.
	private toShiftMinutes(time: string, shiftStart: string): number {
		const minutes = this.timeToMinutes(time);
		const startMinutes = this.timeToMinutes(shiftStart);
		return minutes < startMinutes ? minutes + 24 * 60 : minutes;
	}

	private toTimeWithSeconds(time: string): string {
		if (!time) return time;
		const parts = time.split(':');
		if (parts.length >= 3) return time;
		return `${this.normalizeTime(time)}:00`;
	}

	private toTimeWithoutSeconds(time: string): string {
		if (!time) return '';
		return time.split(':').slice(0, 2).join(':');
	}

	validateBreakTimes(day) {
		day.breakError = day.breakStart === '' || day.breakEnd === '';
	}

	parseDate(date: Date): string {
		const year = date.getFullYear();
		const month = date.getMonth() + 1 < 10 ? `0${String(date.getMonth() + 1)}` : String(date.getMonth() + 1);
		const day = date.getDate() < 10 ? `0${String(date.getDate())}` : String(date.getDate());
		return `${year}-${month}-${day}`;
	}

	parseWeekData(data): IWeekDays {
		const weekDays: IWeekDays = {
			monday: {
				isWorkDay: data[0].isWorkDay,
				workTime: {
					startTime: data[0].startTime,
					endTime: data[0].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[0].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[0].vwwEndTime),
				},
				breaks: this.parseBreaks(data[0].breaks),
			},
			tuesday: {
				isWorkDay: data[1].isWorkDay,
				workTime: {
					startTime: data[1].startTime,
					endTime: data[1].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[1].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[1].vwwEndTime),
				},
				breaks: this.parseBreaks(data[1].breaks),
			},
			wednesday: {
				isWorkDay: data[2].isWorkDay,
				workTime: {
					startTime: data[2].startTime,
					endTime: data[2].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[2].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[2].vwwEndTime),
				},
				breaks: this.parseBreaks(data[2].breaks),
			},
			thursday: {
				isWorkDay: data[3].isWorkDay,
				workTime: {
					startTime: data[3].startTime,
					endTime: data[3].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[3].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[3].vwwEndTime),
				},
				breaks: this.parseBreaks(data[3].breaks),
			},
			friday: {
				isWorkDay: data[4].isWorkDay,
				workTime: {
					startTime: data[4].startTime,
					endTime: data[4].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[4].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[4].vwwEndTime),
				},
				breaks: this.parseBreaks(data[4].breaks),
			},
			saturday: {
				isWorkDay: data[5].isWorkDay,
				workTime: {
					startTime: data[5].startTime,
					endTime: data[5].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[5].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[5].vwwEndTime),
				},
				breaks: this.parseBreaks(data[5].breaks),
			},
			sunday: {
				isWorkDay: data[6].isWorkDay,
				workTime: {
					startTime: data[6].startTime,
					endTime: data[6].endTime,
				},
				variableWeeklyWorkTime: {
					startTime: this.toTimeWithSeconds(data[6].vwwStartTime),
					endTime: this.toTimeWithSeconds(data[6].vwwEndTime),
				},
				breaks: this.parseBreaks(data[6].breaks),
			},
		};
		return weekDays;
	}

	parseBreaks(breaks): ITimeInterval[] {
		const breakList: ITimeInterval[] = [];
		breaks.data.forEach((brk) => {
			const breakObj: ITimeInterval = {
				startTime: brk.startTime,
				endTime: brk.endTime,
			};
			breakList.push(breakObj);
		});
		return breakList;
	}

	createTeamProfile(teamProfile: ITeamProfile) {
		if (this.form.valid) {
			const requestObs = this.teamProfileService.createTeamProfile(teamProfile);
			requestObs.subscribe({
				next: () => {
					this.saved.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.resetEmptyTeamProfileTimesFromNullAfterError();
					this.httpError = `${errorResponse.status}`;
				},
			});
		}
	}

	updateTeamProfile(teamProfile: ITeamProfile) {
		if (this.form.valid) {
			const requestObs = this.teamProfileService.updateTeamProfile(teamProfile);
			requestObs.subscribe({
				next: () => {
					this.saved.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.resetEmptyTeamProfileTimesFromNullAfterError();
					this.httpError = `${errorResponse.status}`;
				},
			});
		}
	}

	resetEmptyTeamProfileTimesFromNullAfterError() {
		this.TeamProfileConfigurations.forEach((configration) => {
			const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
			days.forEach((dayName) => {
				const day = configration.weekDays[dayName];
				day.workTime = day.workTime == null ? { startTime: '', endTime: '' } : day.workTime;
				day.variableWeeklyWorkTime = day.variableWeeklyWorkTime == null ? { startTime: '', endTime: '' } : day.variableWeeklyWorkTime;
			});
		});
	}

	stopPropagation(event: any) {
		event.stopPropagation();
	}

	parseDateFormat(date: string): string {
		return `${date.slice(8)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
	}
}
